/**
 * Sentry error-tracking integration for the React frontend (US237).
 *
 * Provides opt-in Sentry SDK wiring that mirrors the backend configuration.
 * The SDK is only initialised when ``VITE_SENTRY_DSN`` is configured and
 * ``NODE_ENV`` is not ``development``. User context, release tagging, and
 * API error breadcrumbs are handled here so the rest of the application
 * only calls simple helpers.
 */

import {
  init as sentryInit,
  setUser,
  addBreadcrumb,
  browserTracingIntegration,
  replayIntegration,
} from "@sentry/react";

/** Resolve the Sentry DSN from the Vite-inlined env var. */
function getDsn(): string {
  try {
    return (import.meta as any).env?.VITE_SENTRY_DSN || "";
  } catch {
    return "";
  }
}

/** Resolve the Sentry environment, matching the backend convention. */
function getEnvironment(): string {
  try {
    return (import.meta as any).env?.VITE_SENTRY_ENVIRONMENT || "production";
  } catch {
    return "production";
  }
}

/** Resolve the Sentry release, matching the backend convention. */
function getRelease(): string {
  try {
    return (import.meta as any).env?.VITE_SENTRY_RELEASE || "";
  } catch {
    return "";
  }
}

let initialized = false;

/**
 * Initialise the Sentry SDK if a DSN is configured and we are not in
 * development mode.
 */
export function initSentry(): void {
  if (initialized) return;

  const dsn = getDsn();
  const isDev = process.env.NODE_ENV === "development";

  if (!dsn || isDev) {
    if (isDev) {
      console.log("[Sentry] Disabled in development mode.");
    }
    return;
  }

  sentryInit({
    dsn,
    release: getRelease(),
    environment: getEnvironment(),
    integrations: [browserTracingIntegration(), replayIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  initialized = true;
}

/**
 * Attach the current user identity to the Sentry scope.
 * Call this whenever the authenticated user changes.
 */
export function setSentryUser(user: {
  id: string | number;
  email?: string;
}): void {
  if (!initialized) return;
  setUser({ id: String(user.id), email: user.email });
}

/** Clear the user identity from the Sentry scope (on logout). */
export function clearSentryUser(): void {
  if (!initialized) return;
  setUser(null);
}

/**
 * Add a breadcrumb for an API error (5xx response).
 * Call this from the Axios response interceptor.
 */
export function addApiErrorBreadcrumb(
  url: string,
  status: number,
  method: string,
): void {
  if (!initialized) return;
  addBreadcrumb({
    category: "http",
    message: `${method.toUpperCase()} ${url} returned ${status}`,
    level: "error",
    data: { url, status, method },
  });
}

// Re-export for convenience
export { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
